import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  useTheme,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const EditAbsensiModal = ({ open, dataEdit, onClose, onSubmit }) => {
  const theme = useTheme();

  const typeAbsensiOptions = [
    {
      id: 1,
      name: "Shift 1 Pagi",
      time: "07:00:00",
      type_absen: "Double Absen",
    },
    {
      id: 2,
      name: "Shift 2 Siang",
      time: "14:00:00",
      type_absen: "Double Absen",
    },
    {
      id: 3,
      name: "Shift Middle",
      time: "11:00:00",
      type_absen: "Double Absen",
    },
    {
      id: 4,
      name: "Shift 4",
      time: "08:00:00",
      time2: "15:00:00",
      type_absen: "Triple Absen",
    },
  ];

  const formik = useFormik({
    initialValues: {
      shift_absen: "",
      mode_absen: "",
      telat_menit: 0,
      waktu_masuk: "",
      waktu_kedua: "",
      waktu_keluar: "",
    },
    onSubmit: (values) => {
      const reqData = {
        shift_absen: values.shift_absen,
        mode_absen: values.mode_absen,
        telat_menit: values.telat_menit,
        waktu_masuk: values.waktu_masuk,
        waktu_kedua: values.waktu_kedua,
        waktu_keluar: values.waktu_keluar,
      };
      onSubmit(reqData);
      formik.resetForm();
      onClose();
    },
  });

  useEffect(() => {
    if (open && dataEdit) {
      formik.setValues({
        shift_absen: dataEdit.shift_absen || "",
        mode_absen: dataEdit.mode_absen || "",
        telat_menit: dataEdit.telat_menit || 0,
        waktu_masuk: dataEdit.waktu_masuk || "",
        waktu_kedua: dataEdit.waktu_kedua || "",
        waktu_keluar: dataEdit.waktu_keluar || "",
      });
    } else if (!open) {
      formik.resetForm();
    }
  }, [open, dataEdit]);

  useEffect(() => {
    const selectedShift = typeAbsensiOptions.find(
      (item) => item.name === formik.values.shift_absen,
    );
    formik.setFieldValue("mode_absen", selectedShift?.type_absen || "");

    if (selectedShift && formik.values.waktu_masuk) {
      const startTime = moment(selectedShift.time, "HH:mm:ss");
      const checkInTime = moment(formik.values.waktu_masuk, "HH:mm:ss");

      const lateCheckIn = Math.max(0, checkInTime.diff(startTime, "minutes"));

      let lateCheckout = 0;

      const isShift4 = !!selectedShift.time2;

      if (isShift4 && formik.values.waktu_kedua) {
        const endTime = moment(selectedShift.time2, "HH:mm:ss");
        const checkSecondTime = moment(formik.values.waktu_kedua, "HH:mm:ss");

        if (endTime.isValid() && checkSecondTime.isValid()) {
          lateCheckout = Math.max(0, checkSecondTime.diff(endTime, "minutes"));
        }
      }
      formik.setFieldValue("telat_menit", lateCheckIn + lateCheckout);
    }
  }, [
    formik.values.shift_absen,
    formik.values.waktu_masuk,
    formik.values.waktu_kedua,
  ]);

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Absensi</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Shift Absen"
                name="shift_absen"
                value={formik.values.shift_absen}
                onChange={formik.handleChange}
                error={
                  formik.touched.shift_absen &&
                  Boolean(formik.errors.shift_absen)
                }
                helperText={
                  formik.touched.shift_absen && formik.errors.shift_absen
                }
              >
                {typeAbsensiOptions.map((item) => (
                  <MenuItem
                    key={item.name}
                    value={item.name}
                    disabled={item.type_absen !== dataEdit?.mode_absen}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="Mode Absen"
                name="mode_absen"
                value={formik.values.mode_absen}
                onChange={formik.handleChange}
                error={
                  formik.touched.mode_absen && Boolean(formik.errors.mode_absen)
                }
                helperText={
                  formik.touched.mode_absen && formik.errors.mode_absen
                }
              />
            </Grid>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Grid item xs={12}>
                <TimePicker
                  sx={{ width: "100%" }}
                  label="Waktu Masuk"
                  value={
                    formik.values.waktu_masuk
                      ? moment(formik.values.waktu_masuk, "HH:mm:ss")
                      : null
                  }
                  format="HH:mm:ss"
                  onChange={(val) => {
                    formik.setFieldValue(
                      "waktu_masuk",
                      val ? val.format("HH:mm:ss") : "",
                    );
                  }}
                />
              </Grid>
              {dataEdit?.status === "Absen Sore" && (
                <>
                  {formik.values.mode_absen === "Triple Absen" && (
                    <Grid item xs={12}>
                      <TimePicker
                        sx={{ width: "100%" }}
                        label="Waktu Kedua"
                        value={
                          formik.values.waktu_kedua
                            ? moment(formik.values.waktu_kedua, "HH:mm:ss")
                            : null
                        }
                        format="HH:mm:ss"
                        onChange={(val) => {
                          formik.setFieldValue(
                            "waktu_kedua",
                            val ? val.format("HH:mm:ss") : "",
                          );
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TimePicker
                      sx={{ width: "100%" }}
                      label="Waktu Pulang"
                      value={
                        formik.values.waktu_keluar
                          ? moment(formik.values.waktu_keluar, "HH:mm:ss")
                          : null
                      }
                      format="HH:mm:ss"
                      onChange={(val) => {
                        formik.setFieldValue(
                          "waktu_keluar",
                          val ? val.format("HH:mm:ss") : "",
                        );
                      }}
                    />
                  </Grid>
                </>
              )}
            </LocalizationProvider>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="Menit Telat"
                name="telat_menit"
                value={formik.values.telat_menit}
                onChange={formik.handleChange}
                error={
                  formik.touched.telat_menit &&
                  Boolean(formik.errors.telat_menit)
                }
                helperText={
                  formik.touched.telat_menit && formik.errors.telat_menit
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            onClick={onClose}
            sx={{ fontSize: "14px", fontWeight: "bold", padding: "10px 20px" }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.background.alt,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: theme.palette.secondary[500],
              },
            }}
          >
            Kirim
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditAbsensiModal;
